import { useEffect, useRef, useState } from "react";

export interface UserLocationPosition {
  lat: number;
  lng: number;
  accuracy: number;
  // Só reflete um `coords.heading` recente e confiável (ver
  // MIN_SPEED_FOR_HEADING_MPS abaixo) — nunca inventado. Enquanto o
  // dispositivo está parado (heading do GPS vira ruído), mantém a última
  // direção confiável já observada em vez de zerar/resetar visualmente; só
  // fica `null` antes de qualquer heading confiável ter sido visto.
  heading: number | null;
  speed: number | null;
}

export type UserLocationStatus =
  | "idle"
  | "requesting"
  | "granted"
  | "denied"
  | "unsupported"
  | "error";

// Abaixo dessa velocidade (m/s) o `coords.heading` do GPS costuma ser ruído
// (parado ou andando bem devagar) — não usamos pra rotacionar o marcador.
const MIN_SPEED_FOR_HEADING_MPS = 0.5;

// Geolocalização do navegador (Geolocation API), só client-side — nunca
// envia a posição pro backend. Usada exclusivamente pra: (1) posicionar o
// personagem no mapa do PWA e (2) calcular a distância até o veículo
// selecionado, ambos calculados no próprio navegador.
//
// `enabled` liga/desliga o watch — quem chama passa `showMap` (aba Mapa
// ativa), pra não pedir permissão de localização assim que o app abre nem
// manter o GPS ligado em segundo plano nas outras abas.
export function useUserLocation(enabled: boolean) {
  const [status, setStatus] = useState<UserLocationStatus>("idle");
  const [position, setPosition] = useState<UserLocationPosition | null>(null);
  const watchIdRef = useRef<number | null>(null);
  const lastHeadingRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return;
    }
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setStatus("unsupported");
      return;
    }

    setStatus((prev) => (prev === "granted" ? prev : "requesting"));
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy, heading, speed } = pos.coords;
        const headingIsReliable =
          heading != null && !Number.isNaN(heading) && (speed ?? 0) >= MIN_SPEED_FOR_HEADING_MPS;
        if (headingIsReliable) lastHeadingRef.current = heading;
        setPosition({
          lat: latitude,
          lng: longitude,
          accuracy,
          heading: lastHeadingRef.current,
          speed: speed ?? null,
        });
        setStatus("granted");
      },
      (err) => {
        setStatus(err.code === err.PERMISSION_DENIED ? "denied" : "error");
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );
    watchIdRef.current = watchId;

    return () => {
      navigator.geolocation.clearWatch(watchId);
      watchIdRef.current = null;
    };
  }, [enabled]);

  return { status, position };
}
